import { Loader } from "../../loaders/assets/loader";
import * as THREE from "three";
import CameraService from "../camera/camera.service";
import { getProxiedSoundUrl } from "../../../../utils/imageProxy";
import type { RuntimeMediaDelivery, RuntimeMediaManifest } from "../../../../utils/runtimeConfig";
import { resolveRuntimeMediaUrl } from "../../../../utils/runtimeMedia";
import { getVisitorBackgroundMusicMuted } from "../../../../utils/visitorPreferences";

import AppData from "../../app.data";
const appData = new AppData()
const BASE_API = process.env.REACT_APP_BASE_API || '';

type IntroSoundData = {
    url?: string;
    soundVolume?: number;
    runtimeMedia?: RuntimeMediaManifest;
} | null | undefined;

type IntroSoundPreload = {
    url: string;
    promise: Promise<AudioBuffer | null>;
    buffer: AudioBuffer | null;
};

/**
 * Manages spatial audio in the 3D environment
 * Handles loading, playing, and controlling positional sounds
 * Provides mute/unmute functionality and resource cleanup
 */
class SoundService {
    /** Asset loader for sound files */
    private loader: Loader;
    /** Three.js audio listener attached to the camera */
    private listener = null as THREE.AudioListener | null;
    /** Array of positional audio sources in the scene */
    public sounds: THREE.PositionalAudio[] = [];
    /** Current mute state */
    public muted: boolean;
    /** Camera service for audio listener positioning */
    private cameraService: CameraService

    // background sound 
    private backgroundSound: THREE.Audio | null = null;
    private introSound: THREE.Audio | null = null;
    private introSoundGeneration = 0;
    private introSoundPreload: IntroSoundPreload | null = null;
    private runtimeMediaDelivery: RuntimeMediaDelivery | null = null;
    private audioUnlockListenersInstalled = false;
    private readonly audioUnlockHandler = () => {
        void this.resumeAudioContext();
        this.removeAudioUnlockListeners();
    };

    /**
     * Initializes the sound service with required dependencies
     * Sets up audio listener and attaches it to the camera
     * @param loader Asset loader instance
     * @param cameraService Camera service for audio listener attachment
     */
    constructor(loader: Loader, cameraService: CameraService) {
        this.listener = new THREE.AudioListener();
        this.loader = loader;
        this.cameraService = cameraService;
        appData.camera.add(this.listener);
    }

    public setRuntimeMediaDelivery(delivery: RuntimeMediaDelivery | null) {
        this.runtimeMediaDelivery = delivery ? {
            ...delivery,
            families: [...delivery.families],
        } : null;
    }

    private resolvePlaybackUrl(url: string, runtimeMedia?: RuntimeMediaManifest) {
        const runtimeUrl = resolveRuntimeMediaUrl(url, "audio", this.runtimeMediaDelivery, runtimeMedia) || url;
        const cdnDomain = this.runtimeMediaDelivery?.cdnDomain;
        return cdnDomain
            ? getProxiedSoundUrl(runtimeUrl, BASE_API, [cdnDomain])
            : getProxiedSoundUrl(runtimeUrl, BASE_API);
    }

    private installAudioUnlockListeners() {
        if (this.audioUnlockListenersInstalled || typeof window === "undefined") {
            return;
        }

        this.audioUnlockListenersInstalled = true;
        window.addEventListener("pointerdown", this.audioUnlockHandler, { once: true, passive: true });
        window.addEventListener("touchstart", this.audioUnlockHandler, { once: true, passive: true });
        window.addEventListener("keydown", this.audioUnlockHandler, { once: true });
    }

    private removeAudioUnlockListeners() {
        if (!this.audioUnlockListenersInstalled || typeof window === "undefined") {
            return;
        }

        this.audioUnlockListenersInstalled = false;
        window.removeEventListener("pointerdown", this.audioUnlockHandler);
        window.removeEventListener("touchstart", this.audioUnlockHandler);
        window.removeEventListener("keydown", this.audioUnlockHandler);
    }

    public resumeAudioContext = async () => {
        try {
            const audioContext = (this.listener as any)?.context;
            if (audioContext?.state === "suspended" && typeof audioContext.resume === "function") {
                await audioContext.resume();
            }
        } catch (_error) {
            // Browser autoplay restrictions are best-effort; never block gallery activation.
        }
    }

    public enableAudioUnlockOnUserGesture() {
        this.installAudioUnlockListeners();
    }

    private clearBackgroundSound() {
        if (!this.backgroundSound) {
            return;
        }

        try {
            this.backgroundSound.stop();
        } catch (_error) {
            // best-effort cleanup
        }

        try {
            this.backgroundSound.disconnect();
        } catch (_error) {
            // best-effort cleanup
        }

        try {
            this.backgroundSound.setBuffer(null);
        } catch (_error) {
            // best-effort cleanup
        }

        this.backgroundSound = null;
    }

    private clearIntroSound() {
        if (!this.introSound) {
            return;
        }

        try {
            this.introSound.stop();
        } catch (_error) {
            // best-effort cleanup
        }

        try {
            this.introSound.disconnect();
        } catch (_error) {
            // best-effort cleanup
        }

        try {
            this.introSound.setBuffer(null);
        } catch (_error) {
            // best-effort cleanup
        }

        this.introSound = null;
    }

    private disposeRuntimeSound(sound: THREE.PositionalAudio | null | undefined) {
        if (!sound) {
            return;
        }

        try {
            sound.stop();
        } catch (_error) {
            // best-effort cleanup
        }

        try {
            sound.parent?.remove(sound);
        } catch (_error) {
            // best-effort cleanup
        }

        try {
            sound.disconnect();
        } catch (_error) {
            // best-effort cleanup
        }

        try {
            sound.setBuffer(null);
        } catch (_error) {
            // best-effort cleanup
        }
    }

    public cleanupRuntimeSound(sound: THREE.PositionalAudio | null | undefined) {
        if (!sound) {
            return;
        }

        this.disposeRuntimeSound(sound);
        this.sounds = this.sounds.filter((currentSound) => currentSound !== sound);
        appData.soundList = (appData.soundList || []).filter((currentSound) => currentSound !== sound);
    }

    public async loadBackgroundSound() {
        try { 
            this.clearBackgroundSound();

            // get sound data from app data
            if (!appData.galleryData.data || !appData.galleryData.data.sound) {
                return;
            }

            const { url, soundVolume, muted, runtimeMedia } = appData.galleryData.data.sound;
            const effectiveMuted = Boolean(muted) || getVisitorBackgroundMusicMuted();
            if (!url || effectiveMuted) {
                this.muted = effectiveMuted;
                return;
            }
            
            this.muted = false;
            const resolvedSoundVolume = Number.isFinite(Number(soundVolume))
                ? Number(soundVolume)
                : 1;
            const playbackUrl = this.resolvePlaybackUrl(url, runtimeMedia);
            if (!playbackUrl) {
                return;
            }

            // load new background sound
            const soundBuffer = await this.loader.loadSound(playbackUrl) as AudioBuffer;
            this.backgroundSound = new THREE.Audio(this.listener);
            this.backgroundSound.setBuffer(soundBuffer);

            // play new background sound if not muted
            if (!this.muted) {
                await this.resumeAudioContext();
                this.backgroundSound.setLoop(true);
                this.backgroundSound.setVolume(resolvedSoundVolume);
                this.backgroundSound.play();
            }

        } catch (_error) {
            console.log("failed to load background sound");
        }
    }

    public preloadIntroSound(soundData?: IntroSoundData) {
        if (!soundData?.url) {
            this.introSoundPreload = null;
            return Promise.resolve(null);
        }
        const playbackUrl = this.resolvePlaybackUrl(soundData.url, soundData.runtimeMedia);
        if (!playbackUrl) {
            this.introSoundPreload = null;
            return Promise.resolve(null);
        }

        if (this.introSoundPreload?.url === playbackUrl) {
            return this.introSoundPreload.promise;
        }

        const preload: IntroSoundPreload = {
            url: playbackUrl,
            buffer: null,
            promise: Promise.resolve(null),
        };

        preload.promise = (this.loader.loadSound(playbackUrl) as Promise<AudioBuffer>)
            .then((buffer) => {
                preload.buffer = buffer;
                return buffer;
            })
            .catch((_error) => {
                console.log("failed to preload loading intro sound");
                if (this.introSoundPreload === preload) {
                    this.introSoundPreload = null;
                }
                return null;
            });

        this.introSoundPreload = preload;
        return preload.promise;
    }

    private async playIntroBuffer(buffer: AudioBuffer, soundData: IntroSoundData, generation: number, isIntroActive: () => boolean) {
        if (generation !== this.introSoundGeneration || !isIntroActive()) {
            return;
        }

        await this.resumeAudioContext();
        if (generation !== this.introSoundGeneration || !isIntroActive()) {
            return;
        }

        this.clearIntroSound();
        this.introSound = new THREE.Audio(this.listener);
        this.introSound.setBuffer(buffer);
        this.introSound.setLoop(true);
        this.introSound.setVolume(Number.isFinite(Number(soundData?.soundVolume)) ? Number(soundData?.soundVolume) : 1);

        try {
            this.introSound.play();
        } catch (_error) {
            console.log("failed to play loading intro sound");
            this.clearIntroSound();
        }
    }

    public async playIntroSound(soundData?: IntroSoundData, isIntroActive: () => boolean = () => true) {
        const generation = ++this.introSoundGeneration;
        this.clearIntroSound();
        if (!soundData?.url) {
            return;
        }

        const playbackUrl = this.resolvePlaybackUrl(soundData.url, soundData.runtimeMedia);
        if (!playbackUrl) {
            return;
        }
        const preload = this.preloadIntroSound(soundData);
        const preloadedBuffer = this.introSoundPreload?.url === playbackUrl
            ? this.introSoundPreload.buffer
            : null;

        if (preloadedBuffer) {
            await this.playIntroBuffer(preloadedBuffer, soundData, generation, isIntroActive);
            return;
        }

        void preload.then((buffer) => {
            if (!buffer) return;
            void this.playIntroBuffer(buffer, soundData, generation, isIntroActive);
        });
    }

    public async stopIntroSound(fadeMs = 250) {
        this.introSoundGeneration++;
        const sound = this.introSound;
        if (!sound) {
            return;
        }

        const finish = () => {
            if (this.introSound === sound) {
                this.clearIntroSound();
            } else {
                try {
                    sound.stop();
                    sound.disconnect();
                    sound.setBuffer(null);
                } catch (_error) {
                    // best-effort cleanup
                }
            }
        };

        if (!sound.isPlaying || fadeMs <= 0 || typeof window === "undefined" || !window.requestAnimationFrame) {
            finish();
            return;
        }

        const startVolume = typeof sound.getVolume === "function" ? sound.getVolume() : 1;
        const startedAt = performance.now();
        await new Promise<void>((resolve) => {
            const step = (now: number) => {
                const progress = Math.min(1, (now - startedAt) / fadeMs);
                sound.setVolume(startVolume * (1 - progress));
                if (progress < 1 && this.introSound === sound) {
                    window.requestAnimationFrame(step);
                    return;
                }
                finish();
                resolve();
            };
            window.requestAnimationFrame(step);
        });
    }

    public async loadSoundInSlot(slot: any, soundData: any, activeArtwork: any, loadedMesh: any) {
        try {
            const playbackUrl = this.resolvePlaybackUrl(soundData.dataUrl, soundData.runtimeMedia);
            if (!playbackUrl) {
                throw new Error('Sound media reference rejected');
            }
            const soundBuffer = await this.loader.loadSound(playbackUrl) as AudioBuffer;
            const sound = new THREE.PositionalAudio(this.listener);
            sound.setBuffer(soundBuffer);
            sound.setRefDistance(3);
            sound.setRolloffFactor(2);
            sound.setMaxDistance(15);

            sound.userData = { 
                activeArtwork: activeArtwork, 
                loadedMesh: loadedMesh, 
                index: this.sounds.length 
            }
            
            // compute resulting sound position
            const positionDummy = new THREE.Object3D()
            positionDummy.position.copy(slot.position)
            positionDummy.rotation.copy(slot.rotation)
            positionDummy.translateX(activeArtwork.transforms.position.x)
            positionDummy.translateY(activeArtwork.transforms.position.y)
            positionDummy.translateZ(activeArtwork.transforms.position.z)

            sound.position.copy(positionDummy.position)
            sound.rotation.copy(positionDummy.rotation)

            appData.artworksScene.add(sound);

            sound.updateMatrixWorld(true);


            // push sound to sounds array 
            this.sounds.push(sound);
            
            return sound;
        } catch (_error) {
            console.error("Sound slot load failed");
            throw new Error('Sound slot load failed');
        }
    }

    // pause/resume background sound
    public async playBackgroundSound() {
        if (!this.backgroundSound ) {
            await this.loadBackgroundSound();
            return
        }

        if (this.muted || this.backgroundSound.isPlaying) {
            return;
        }
        void this.resumeAudioContext().then(() => {
            if (!this.backgroundSound || this.muted || this.backgroundSound.isPlaying) {
                return;
            }
            this.backgroundSound.play();
        });
    }

    public pauseBackgroundSound() {
        if (!this.backgroundSound || this.muted || !this.backgroundSound.isPlaying) {
            return;
        }
        this.backgroundSound.pause();
    }

    /**
     * Pauses all active sound sources
     * Handles errors gracefully if any sound fails to pause
     */
    public pauseSounds() {
        try {
            const sounds = [...(appData.soundList || [])];
            sounds.forEach((sound) => this.cleanupRuntimeSound(sound));
        } catch (_error) {
            console.log("Sound pause cleanup failed");
        }
    }

    /**
     * Mutes all sounds by pausing background sound and setting the muted flag
     */
    public mute() {
        if (this.backgroundSound) {
            this.backgroundSound.pause();
        }
        this.muted = true;
    }

    /**
     * Unmutes all sounds by clearing the muted flag and playing background sound
     */
    public async unmute() {
        this.muted = false;
        await this.playBackgroundSound();
    }

    /**
     * Plays all sound sources if not muted
     * Handles errors gracefully if any sound fails to play
     */
    public playSounds() {
        try {
            if (this.muted) {
                return;
            }

            appData.soundList.forEach((sound) => {
                if (sound) {
                    sound.play();
                }
            })
        } catch (_error) {
            console.log("Sound playback failed");
        }
    }

    /**
     * Stops and cleans up all sound sources
     * Disconnects audio nodes and releases buffers
     */
    public stopSounds() {
        try {
            const sounds = Array.from(new Set([...(appData.soundList || []), ...this.sounds]));
            sounds.forEach((sound) => this.cleanupRuntimeSound(sound));
            appData.soundList = [];
            this.sounds = [];
        } catch (_error) {
            console.log("Sound stop cleanup failed");
        }
    }

    /**
     * Removes audio listener from camera
     * Called during cleanup
     */
    public dispose = () => {
        try {
            this.stopSounds();
            this.clearBackgroundSound();
            this.clearIntroSound();
            this.removeAudioUnlockListeners();
            if (this.listener) {
                appData.camera?.remove(this.listener);
                this.listener = null;
            }
            this.sounds = [];
        } catch (_error) {
            console.log("Sound disposal failed");
        }
    };

    /**
     * Placeholder for additional cleanup if needed
     */
    public destroy() {
        this.stopSounds();
        this.clearBackgroundSound();
        this.clearIntroSound();
        this.removeAudioUnlockListeners();
    }
}

export default SoundService;
